// Auth state — Zustand store managing Keycast session lifecycle.
// Status machine: idle → loading → ready | error | needs-setup.
// Ported from client/client/src/state/useAuth.ts (simplified — no migration, no support sessions).

import { create } from 'zustand'
import * as keycast from '@/lib/keycastClient'
import { useAdminStatus } from './useAdminStatus'
import { useTeam, readSupportSessionIds, writeSupportSessionIds } from './useTeam'
import { useBilling } from './useBilling'

const LS_PUBKEY = 'synvya_personal_pubkey'

export type AuthStatus = 'idle' | 'loading' | 'ready' | 'error' | 'needs-setup'

interface AuthState {
  status: AuthStatus
  personalPubkey: string | null
  npub: string | null
  email: string | null
  displayName: string | null
  isAuthenticated: boolean
  verificationRequired: boolean
  error: string | null

  initialize: () => Promise<void>
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string) => Promise<void>
  setDisplayName: (name: string) => void
  logout: () => Promise<void>
}

export const useAuth = create<AuthState>((set, get) => ({
  status: 'idle',
  personalPubkey: null,
  npub: null,
  email: localStorage.getItem('synvya_user_email') ?? null,
  displayName: localStorage.getItem('synvya_display_name') ?? null,
  isAuthenticated: false,
  verificationRequired: false,
  error: null,

  initialize: async () => {
    if (get().status === 'loading') return
    set({ status: 'loading', error: null })

    try {
      const teams = await keycast.listTeams()
      const cached = localStorage.getItem(LS_PUBKEY)
      if (cached) {
        // Validate: if the server returned teams, the cached pubkey must
        // appear in at least one team's members. A stale pubkey (or a
        // server that returns 200 without auth) would show teams the user
        // isn't part of — clear the pubkey and force re-login.
        //
        // EXCEPTION: support/admin users legitimately access teams they are
        // NOT members of (JIT grant — they never appear in team_users). For
        // them, non-membership is expected, not a stale session. Probe admin
        // status before logging out so we don't kick support users on every
        // reload (they typically see many teams, none with their pubkey).
        if (teams.length > 0) {
          const userInTeams = teams.some((t) =>
            t.team_users?.some((u) => u.user_pubkey === cached),
          )
          if (!userInTeams) {
            const admin = await keycast.adminStatus().catch(() => null)
            if (!admin?.is_admin) {
              localStorage.removeItem(LS_PUBKEY)
              set({ status: 'needs-setup' })
              return
            }
          }
        }
        set({
          status: 'ready',
          personalPubkey: cached,
          isAuthenticated: true,
        })
      } else {
        set({ status: 'needs-setup' })
      }
    } catch (err) {
      if (keycast.isUnauthorized(err) || keycast.isForbidden(err)) {
        // 401 (session expired) or 403 (team access revoked) → re-auth
        set({ status: 'needs-setup', isAuthenticated: false })
      } else {
        const msg = err instanceof Error ? err.message : 'Auth initialization failed'
        set({ status: 'error', error: msg, isAuthenticated: false })
      }
    }
  },

  login: async (email: string, password: string) => {
    set({ status: 'loading', error: null })
    try {
      const res = await keycast.login(email, password)
      const pubkey = res.pubkey ?? null
      if (pubkey) {
        localStorage.setItem(LS_PUBKEY, pubkey)
      }
      const userEmail = res.email ?? email
      localStorage.setItem('synvya_user_email', userEmail)
      set({
        status: 'ready',
        personalPubkey: pubkey,
        email: userEmail,
        isAuthenticated: true,
      })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Login failed'
      set({ status: 'error', error: msg, isAuthenticated: false })
      throw err
    }
  },

  register: async (email: string, password: string) => {
    set({ status: 'loading', error: null })
    try {
      const res = await keycast.register(email, password)

      // Email verification required — don't mark as authenticated yet
      if (res.verification_required) {
        set({
          status: 'needs-setup',
          email: res.email ?? email,
          isAuthenticated: false,
          verificationRequired: true,
        })
        return
      }

      const pubkey = res.pubkey ?? null
      if (pubkey) {
        localStorage.setItem(LS_PUBKEY, pubkey)
      }
      const userEmail = res.email ?? email
      localStorage.setItem('synvya_user_email', userEmail)
      set({
        status: 'ready',
        personalPubkey: pubkey,
        email: userEmail,
        isAuthenticated: true,
        verificationRequired: false,
      })
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Registration failed'
      set({ status: 'error', error: msg, isAuthenticated: false })
      throw err
    }
  },

  setDisplayName: (name: string) => {
    const trimmed = name.trim()
    localStorage.setItem('synvya_display_name', trimmed)
    set({ displayName: trimmed || null })
  },

  logout: async () => {
    // Release support sessions (fire-and-forget, mirrors v1)
    const supportIds = readSupportSessionIds()
    for (const id of supportIds) {
      keycast.releaseSupportAccess(id).catch(() => {})
    }
    writeSupportSessionIds([])

    try {
      await keycast.logout()
    } catch {
      // fire-and-forget
    }
    // Clear admin + team stores so next login re-probes (mirrors v1)
    useAdminStatus.getState().reset()
    useTeam.getState().reset()
    useBilling.getState().reset()
    // Clear all session-scoped synvya_ keys; preserve device-scoped
    // bunker credentials (URL + NIP-46 client key). Matches v1 pattern —
    // comprehensive clear catches future keys without individual removeItem.
    const PRESERVE_ON_LOGOUT = new Set([
      'synvya_bunker_url',
      'synvya_nip46_client_key',
    ])
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('synvya_') && !PRESERVE_ON_LOGOUT.has(key)) {
        localStorage.removeItem(key)
      }
    }
    set({
      status: 'needs-setup',
      personalPubkey: null,
      npub: null,
      email: null,
      displayName: null,
      isAuthenticated: false,
      verificationRequired: false,
      error: null,
    })
  },
}))
